import React, { useState } from 'react';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import zoomStyles from './index.module.scss';

/**
 * ZoomableImage Component
 * 
 * A wrapper component that provides zoom and pan functionality for images using the react-zoom-pan-pinch library.
 * Supports touch gestures, mouse wheel zoom, and drag panning with smooth animations and boundary constraints.
 * 
 * @component
 * @param {Object} props - Component props
 * @param {string} props.src - Image source URL
 * @param {string} [props.alt=''] - Alternative text for the image
 * @param {string} [props.className=''] - CSS class name to apply to the image
 * @param {React.ReactNode} [props.children] - Optional children to render instead of the default image element
 * @param {Object} props... - Additional props passed to the img element
 * 
 * @example
 * <ZoomableImage 
 *   src="https://example.com/image.jpg" 
 *   alt="Example image"
 *   className="my-image"
 * />
 * 
 * Configuration details for TransformWrapper:
 * 
 * Scale Settings:
 * - initialScale: 1 - Starting zoom level (1 = 100%, no zoom)
 * - minScale: 1 - Minimum zoom level (prevents zooming out beyond original size)
 * - maxScale: 8 - Maximum zoom level (allows up to 800% zoom)
 * 
 * Interaction Settings:
 * - smooth: true - Enables smooth transitions between zoom/pan states
 * - doubleClick.disabled: true - Disables zoom on double-click/tap
 * 
 * Wheel (Scroll/Trackpad) Settings:
 * - wheel.step: 2 - Amount of zoom per scroll step
 * - wheel.smooth: 1 - Smoothness of wheel zoom animation (0-1)
 * - wheel.wheelEnabled: true - Enables mouse wheel zooming
 * - wheel.touchPadEnabled: true - Enables trackpad pinch-to-zoom gestures
 * - wheel.activationKeys: [] - No modifier keys required for wheel zoom
 * - wheel.excluded: [] - No DOM elements excluded from wheel events
 * 
 * Pinch (Touch Gesture) Settings:
 * - pinch.step: 2 - Amount of zoom per pinch gesture step
 * - pinch.smooth: 1 - Smoothness of pinch zoom animation (0-1)
 * - pinch.normalizeWheel: true - Normalizes wheel delta across different browsers/devices
 * 
 * Boundary Settings:
 * - limitToBounds: true - Prevents panning beyond image boundaries
 * - boundaryFriction: 0 - No friction when hitting boundaries (hard stop)
 * - centerOnInit: true - Centers the image when first loaded
 * - centerZoomedOut: true - Centers image when zoomed out to minimum scale
 * - disablePadding: true - Disables internal padding in the transform container
 * 
 * Animation Settings:
 * - alignmentAnimation.sizeX: 0 - No horizontal alignment animation
 * - alignmentAnimation.sizeY: 0 - No vertical alignment animation
 * - alignmentAnimation.velocityAlignmentTime: 0 - Instant alignment (no delay)
 * - velocityAnimation.sensitivity: 0.5 - Sensitivity of velocity-based movements
 * - velocityAnimation.animationTime: 100 - Duration of velocity animations (ms)
 * - velocityAnimation.animationType: 'linear' - Linear easing for velocity animations
 * 
 * Panning Settings:
 * - panning.disabled: false - Panning is enabled
 * - panning.velocityDisabled: true - Disables velocity-based panning (drag momentum)
 * - panning.lockAxisX: false - Allows horizontal panning
 * - panning.lockAxisY: false - Allows vertical panning
 * - panning.allowLeftClickPan: true - Enables panning with left mouse button/touch
 * - panning.allowRightClickPan: false - Disables panning with right mouse button
 * - panning.allowMiddleClickPan: false - Disables panning with middle mouse button
 * - panning.wheelPanning: false - Disables panning with scroll wheel
 * 
 * TransformComponent Styles:
 * - wrapperStyle: Styles for the outer wrapper (100% width/height, hidden overflow, rounded corners)
 * - contentStyle: Styles for the content container (flex centered layout)
 */

function ZoomableImage({
  src,
  alt = '',
  className = '',
  children,
  ...props
}) {

  const [scale, setScale] = useState(1);

  const imageElement = (
    <img
      src={src}
      alt={alt}
      className={`${className} ${scale <= 1 ? zoomStyles.touchPanY : zoomStyles.touchNone}`}
      draggable={false}
      onDragStart={(e) => e.preventDefault()}
      {...props}
    />
  );

  return (
    <TransformWrapper
      initialScale={1}
      minScale={1}
      maxScale={8}
      smooth={true}
      doubleClick={{
        disabled: true,
      }}
      wheel={{
        step: 2, // Zoom increment per scroll (number > 0, typically 0.1-5). Higher = more aggressive zoom per scroll tick
        smooth: 1,
        wheelEnabled: true,
        touchPadEnabled: true,
        activationKeys: [],
        excluded: [],
      }}
      pinch={{
        step: 2, // Zoom increment per pinch gesture (number > 0, typically 0.1-5). Higher = faster zoom during pinch
        smooth: 1,
        normalizeWheel: true,
      }}
      limitToBounds={true}
      boundaryFriction={0}
      // Alignment animation disabled (all values set to 0) for instant, responsive panning
      // This prevents automatic centering/alignment animations when the image reaches boundaries,
      // providing immediate user feedback and more predictable behavior during drag operations
      alignmentAnimation={{
        sizeX: 0, 
        sizeY: 0,
        velocityAlignmentTime: 0,
      }}
      velocityAnimation={{
        sensitivity: 0.5,
        animationTime: 100,
        animationType: 'linear',
      }}
      centerOnInit={true}
      centerZoomedOut={true}
      disablePadding={true}
      panning={{
        disabled: scale <= 1,
        velocityDisabled: true,
        lockAxisX: false,
        lockAxisY: false,
        allowLeftClickPan: true,
        allowRightClickPan: false,
        allowMiddleClickPan: false,
        wheelPanning: false,
      }}
     onTransformed={(ref) => {
        const currentScale = ref?.state?.scale ?? 1;
        setScale(currentScale);
      }}
    >
      <TransformComponent
        wrapperStyle={{
          width: '100%',
          height: '100%',
          overflow: 'hidden',
          borderRadius: '5%',
          touchAction: scale <= 1 ? 'pan-y' : 'none',
        }}
        contentStyle={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          touchAction: scale <= 1 ? 'pan-y' : 'none',
        }}
      >
        {children || imageElement}
      </TransformComponent>
    </TransformWrapper>
  );
}

export default ZoomableImage;